import { computed, inject, Injectable } from '@angular/core';
import { TAB_VALUE } from '../../../../shared/components/listing-page/listing-page.types';
import {
  ALARM_DEFAULT_SEARCH_TYPE,
  ALARM_SEARCH_TYPES,
  chartAlarmSegmentsColors,
  commodityTableIcon,
  createAlarmsBadgesFn,
  deviceTypeTableIcon
} from '../../../../shared/components/listing-page/services/listing-page-model/listing-page-model.constants';
import {
  ListingDataSource,
  ListingPageModel
} from '../../../../shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { AlarmsListingTableComponent } from '../../../../shared/components/listing-table/alarms-listing-table.component';
import { MapTileComponent } from '../../../../shared/components/map/map.component';
import { LocationColumnComponent } from '../../../../shared/components/table-columns/location-column.component';
import { DeviceType, OrderDirection } from '../../devices/components/devices.models';
import { alarmsTableSortField } from '../alarms.constants';
import { AlarmsService } from './alarm-data.service';
import { AlarmsRows, AlarmsTable } from './alarm-data.types';
import { AlarmListingFieldName } from './alarms.types';
import { TableData } from 'src/app/shared/types/api-response.types';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { BubbleColumnComponent } from 'src/app/shared/components/listing-table/components/bubble-column.component';
import { IconColumnComponent } from 'src/app/shared/components/listing-table/components/icon-column.component';
import { ListingPageModelAbstract } from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model-abstract';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import { ListingPageModelRegistry } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-model-registry.service';
import { ListingPageId } from '@portal/shared/components/listing-page/store/listing-page-store.types';
import { AlarmsListingFiltersSourceService } from './alarms-listing-filters-source.service';
import { ExportRow } from '@portal/shared/types/export.types';
import { Store } from '@ngrx/store';
import { isSingleCommodityClient } from 'src/app/pages/help-and-settings/user-settings/store/user-settings.reducer';
import { CommDeviceIdColumnComponent } from '../../devices/components/column/comm-device-id-column.component';
import { SerialNumberColumnComponent } from '../../devices/components/column/serial-number-column.component';
import { devicesWithoutSerialNumber } from '../../devices/constants/devices.constants';

@Injectable()
export class AlarmsListingModelService extends ListingPageModelAbstract implements ListingPageModel {
  private readonly alarmsService = inject(AlarmsService);
  private readonly tz = inject(TimezoneService);
  readonly listingPageModelRegistry = inject(ListingPageModelRegistry);
  readonly #isSingleCommodityClient = inject(Store).selectSignal(isSingleCommodityClient);

  readonly pageTitle = TranslateRegistry.get.ALARMS.TITLE.ALARMS;
  readonly searchTypeList = computed(() => ALARM_SEARCH_TYPES);
  readonly selectedSearchType = computed(() => ALARM_DEFAULT_SEARCH_TYPE);
  readonly sorting = computed(() => ({
    columnName: AlarmListingFieldName.DateTimeUtcUpdated,
    orderDirection: OrderDirection.DESC
  }));

  readonly tableColumns = computed(() => [
    {
      field: 'dateTime',
      title: TranslateRegistry.get.ALARMS.VIEW_TABLE.DATE_TIME,
      width: 200,
      sortable: true
    },
    {
      field: 'severity',
      title: TranslateRegistry.get.ALARMS.VIEW_TABLE.SEVERITY,
      width: 200,
      sortable: false,
      cmp: BubbleColumnComponent
    },
    {
      field: 'groupName',
      title: TranslateRegistry.get.ALARMS.VIEW_TABLE.ALARM_GROUP,
      width: 200,
      sortable: false
    },
    { field: 'name', title: TranslateRegistry.get.ALARMS.VIEW_TABLE.ALARM_NAME, width: 300, sortable: false },
    {
      field: 'deviceTypeName',
      title: TranslateRegistry.get.ALARMS.VIEW_TABLE.DEVICE_TYPE,
      width: 200,
      sortable: false,
      icon: deviceTypeTableIcon
    },
    {
      field: 'commodity',
      title: TranslateRegistry.get.ALARMS.VIEW_TABLE.COMMODITY,
      width: 200,
      sortable: false,
      icon: commodityTableIcon,
      cmp: IconColumnComponent,
      isHidden: this.#isSingleCommodityClient()
    },
    {
      field: 'commDeviceId',
      title: TranslateRegistry.get.ALARMS.VIEW_TABLE.COMM_DEVICE_ID,
      sortable: false,
      width: 200,
      cmp: CommDeviceIdColumnComponent
    },
    {
      field: 'serialNumber',
      title: TranslateRegistry.get.ALARMS.VIEW_TABLE.SERIAL_NUMBER,
      sortable: false,
      width: 200,
      cmp: SerialNumberColumnComponent
    },
    { field: 'modelName', title: TranslateRegistry.get.ALARMS.VIEW_TABLE.MODEL_NAME, width: 200, sortable: false },
    {
      field: 'location',
      title: TranslateRegistry.get.ALARMS.VIEW_TABLE.LOCATION,
      width: 200,
      sortable: false,
      cmp: LocationColumnComponent
    }
  ]);
  readonly dataSource: ListingDataSource<AlarmsTable> = {
    getAll: tableState => {
      return this.alarmsService.prepareDataForTable({
        ...tableState,
        filters: [
          ...(tableState.filters ?? []).map(f => ({
            ...f,
            filteredValues:
              f.filterType === AlarmListingFieldName.DateTimeUtcUpdated
                ? this.tz.getShiftedDatepickerRangeStringified({
                    start: new Date(f.filteredValues[0]),
                    end: new Date(f.filteredValues[1])
                  })
                : f.filteredValues
          }))
        ]
      });
    },
    total: 0
  };

  readonly exportConfig = {
    fileName: 'Export-Alarms',
    mapData: (data: TableData<AlarmsRows>): ExportRow[] => {
      return data.data.map(alarm => ({
        dateTime: { title: 'Alarm Date/Time', value: alarm.dateTime },
        severity: { title: 'Alarm Severity', value: alarm.severity },
        groupName: { title: 'Alarm Group', value: alarm.groupName },
        name: { title: 'Alarm Name', value: alarm.name },
        deviceTypeName: { title: 'Device Type', value: alarm.deviceTypeName },
        commodity: { title: 'Commodity', value: alarm.commodity },
        commDeviceId: {
          title: 'CommDeviceID',
          value: alarm.commDeviceId ?? alarm.parentCommDeviceId ?? ''
        },
        serialNumber: {
          title: 'SerialNumber',
          value: devicesWithoutSerialNumber.includes(alarm.deviceType as DeviceType)
            ? ''
            : (alarm.serialNumber ?? 'Unknown Serial Number')
        },
        modelName: { title: 'ModelName', value: alarm.modelName },
        location: [
          { title: 'Latitude', value: alarm.geoMetaData?.latitude ?? '' },
          { title: 'Longitude', value: alarm.geoMetaData?.longitude ?? '' },
          { title: 'Comment', value: alarm.geoMetaData?.comments ?? '' }
        ]
      }));
    }
  };

  readonly chartSegmentsColors = chartAlarmSegmentsColors;
  readonly createBadgesFn = createAlarmsBadgesFn;
  readonly mapCmp = MapTileComponent;
  readonly tableCmp = AlarmsListingTableComponent;
  readonly defaultTab = TAB_VALUE.Table;
  readonly sortField = alarmsTableSortField;
  readonly pageId = ListingPageId.Alarms;
  readonly filterSource = inject(AlarmsListingFiltersSourceService);
  constructor() {
    super();
    const listingPageModelRegistry = this.listingPageModelRegistry;

    listingPageModelRegistry.add(this.pageId, this);
  }
}
